// The seam between the engine and a graphics backend.
//
// The client draws through WebGL2 today and through WebGPU where the browser
// gives a device. The two APIs share no object at all, so the engine and
// every layer must speak to a set of INTERFACES and never to one API.
//
// This file holds those interfaces and one figure of the contract. It
// imports no backend, it declares no class and it runs no code, so a test
// reads it in jsdom and `gfx/seam.test.ts` can pin that no backend token
// leaks out of its folder.
//
// `fast/gl/` implements the interfaces over WebGL2 and `fast/gpu/` over
// WebGPU. The backend-neutral code that sits ON the interfaces — the three
// instance batches, the world pass, the atlas and the uniform set — lives
// beside this file in `fast/gfx/` and is written once.

import type { ProgramSource } from '../shaders/program';

/**
 * The texture ceiling the engine assumes where a device states nothing
 * usable, and the atlas's own default ceiling: 2048 px, which every WebGL2
 * and WebGPU device allows. `maxTextureSize()` below answers it then.
 */
export const FALLBACK_MAX_TEXTURE = 2048;

/** Which graphics API the engine draws through. */
export type Backend = 'webgl2' | 'webgpu';

/**
 * What the backend draws into, in device px (R1498): `tex` is the WebGPU
 * canvas texture of the last frame, `dbuf` the WebGL2 drawing buffer.
 */
export interface DrawnSize {
  readonly what: 'tex' | 'dbuf';
  readonly w: number;
  readonly h: number;
}

/**
 * The drawing surface, as a LAYER may see it.
 *
 * It carries the size, the state of the device and the two counters the
 * stats line reads. It carries no draw call: a layer draws through a
 * batch, a pass or a pipeline and never through this object.
 */
export interface Gfx {
  readonly backend: Backend;
  readonly canvas: HTMLCanvasElement;
  /** The drawing surface in CSS px. */
  readonly width: number;
  readonly height: number;
  /** The drawing surface in device px. */
  readonly pixelWidth: number;
  readonly pixelHeight: number;
  /** Device px for one CSS px, capped at 2 or at what `?dpr=N` states. */
  readonly dpr: number;
  /** Did the surface get multisampling? Only WebGL2 with `?aa=1` does. */
  readonly aa: boolean;
  /** Is the device lost right now? A layer must draw nothing while it is. */
  readonly lost: boolean;
  /** The backend's own name for the device, for the stats line. */
  readonly renderer: string;
  /** Draw calls and instances since the process started. `frame.ts` diffs. */
  drawCalls: number;
  instances: number;
  /** Does `?glcheck` ask for an error check after every draw? */
  readonly glcheck: boolean;

  /** Called after the CSS size or the device pixel ratio changed. */
  onResize(fn: () => void): void;
  /** Called after the device came back and every resource was recreated. */
  onRestore(fn: () => void): void;
  /**
   * The size the backend draws into, for the touch log's geometry line
   * (R1498). WebGL2 reads the drawing buffer when asked. WebGPU answers the
   * canvas texture of the last frame it drew, and records it only while
   * `watchDrawn` holds a watcher, because a read of `getCurrentTexture`
   * outside the frame would present a texture of its own.
   */
  drawnSize?(): DrawnSize | null;
  /** Sets the one watcher of a change of the drawn size (WebGPU alone). */
  watchDrawn?(fn: () => void): void;
  /** Dev-only: reads the error state and logs. It stalls the pipeline. */
  check(where: string): void;
  /** Force a device loss, for the recovery test. */
  loseContext(): void;
  restoreContext(): void;
  dispose(): void;
}

/**
 * The surface as the FRAME LOOP sees it.
 *
 * The loop opens and closes the frame here instead of writing raw calls, so
 * one loop drives both backends: WebGL2 sets a viewport and clears, WebGPU
 * opens a render pass.
 */
export interface FrameGfx extends Gfx {
  /** Opens the frame. `clear` fills the surface with `clearRgb` first. */
  beginFrame(clear: boolean, clearRgb: readonly [number, number, number]): void;
  /** Closes the frame and submits it. */
  endFrame(): void;
  /** Turns the one blend state off for one pass, and on again after it. */
  setBlend(on: boolean): void;
  /** GPU ms of the last frame the timer could read, or null without one. */
  readonly gpuMs: number | null;
}

/**
 * The uniform setters of a program, and what a uniform set writes into.
 *
 * The setters write BY NAME. A name the program does not carry costs one
 * map lookup and returns, so a shared uniform contract is safe to
 * over-state and can never under-state (`gfx/uniforms.ts`). A WebGPU packer
 * that writes into a CPU shadow satisfies it as well as a compiled WebGL2
 * program does.
 */
export interface UniformSink {
  uniform1i(name: string, v: number): void;
  uniform1f(name: string, v: number): void;
  uniform2f(name: string, x: number, y: number): void;
  uniform2i(name: string, x: number, y: number): void;
  uniform3f(name: string, x: number, y: number, z: number): void;
  uniform4f(name: string, x: number, y: number, z: number, w: number): void;
  uniform1fv(name: string, v: Float32Array): void;
  uniform3fv(name: string, v: Float32Array): void;
  uniform4fv(name: string, v: Float32Array): void;
  uniformMatrix3fv(name: string, v: Float32Array): void;
}

/** One compiled program, with its uniform setters. */
export interface Pipeline extends UniformSink {
  readonly name: string;
  /** Binds the program. Returns false when the device is lost. */
  use(): boolean;
  has(name: string): boolean;
  /** Points a sampler at a texture unit. */
  sampler(name: string, unit: number): void;
  /** Draws the shared four-corner strip once (R553). */
  drawQuad(): void;
  dispose(): void;
}

/** One attribute of an instance or a vertex layout. */
export interface AttribSpec {
  /** The name the vertex source declares. The WGSL header is built from it. */
  name: string;
  /** The `layout(location=N)` the vertex shader declares. */
  location: number;
  /** How many floats it takes. */
  size: number;
  /** Its first float inside the instance or the vertex. */
  offset: number;
}

/** A growable divisor-1 instance array. The three batches write into it. */
export interface InstanceBuffer {
  data: Float32Array;
  /** How many instances the caller wrote since `begin()`. */
  count: number;
  capacity: number;
  readonly floatsPerInstance: number;
  begin(): void;
  /** Reserves one instance and returns its first float index. */
  reserve(): number;
  /** Uploads and draws, with the pipeline in use. Returns the count. */
  draw(vertexCount: number): number;
  dispose(): void;
}

/** What an instance batch needs beside the surface. */
export interface BatchDeps {
  gfx: Gfx;
  pipeline: Pipeline;
  /** The backend's instance-buffer factory (`GfxBackend.instances`). */
  instances(
    floatsPerInstance: number, attribs: readonly AttribSpec[], capacity: number,
  ): InstanceBuffer;
}

/** A static indexed triangle mesh. The traced border draws from one. */
export interface MeshBuffer {
  set(vertices: Float32Array, indices: Uint32Array): void;
  readonly indexCount: number;
  /** Draws the whole mesh. Returns 1, or 0 when there is nothing to draw. */
  draw(): number;
  dispose(): void;
}

/** The five per-cell formats the engine uses. */
export type DataFormat = 'r8ui' | 'r8' | 'r16f' | 'rg16f' | 'rgba16f';

/** One plane of per-cell data, read by the world passes on its own unit. */
export interface DataTexture {
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly format: DataFormat;
  readonly channels: number;
  readonly linear: boolean;
  readonly data: Uint8Array | Float32Array;
  /** The index of cell (col,row) in `data`, first channel. */
  index(col: number, row: number): number;
  setCell(col: number, row: number, v: number): void;
  setCell2(col: number, row: number, r: number, g: number): void;
  set(src: ArrayLike<number>): void;
  fill(v: number): void;
  markDirty(): void;
  /** Marks rows `r0` to `r1` alone for the next upload (R2897). */
  markRows(r0: number, r1: number): void;
  readonly isDirty: boolean;
  /** Uploads the whole plane, or the rows of `markRows`, and only when it
   *  is dirty. */
  upload(): boolean;
  bind(unit: number): void;
  dispose(): void;
}

/** The sprite sheet, uploaded premultiplied with four mip levels. */
export interface AtlasTexture {
  setSource(source: HTMLCanvasElement): void;
  patch(canvas: HTMLCanvasElement, x: number, y: number): void;
  bind(unit: number): void;
  dispose(): void;
}

/**
 * The primitives, one set per backend.
 *
 * This is the whole of what a backend must implement. Everything else the
 * engine draws with is written once against these interfaces.
 */
export interface GfxBackend {
  readonly gfx: FrameGfx;
  pipeline(src: ProgramSource): Pipeline;
  instances(
    floatsPerInstance: number, attribs: readonly AttribSpec[], capacity: number,
  ): InstanceBuffer;
  mesh(floatsPerVertex: number, attribs: readonly AttribSpec[]): MeshBuffer;
  dataTexture(
    name: string, width: number, height: number, format: DataFormat, linear?: boolean,
  ): DataTexture;
  atlasTexture(source: HTMLCanvasElement): AtlasTexture;
  /** The widest texture the device allows, for the atlas ceiling. */
  maxTextureSize(): number;
}
